import axios from 'axios';
import getFirebaseAuthToken from './fireBaseAuthenticationService';
import { BACKEND_URL, TIMESHEET_POST_TIMEOUT } from '../utils/constants';
import useUserStore from '../stores/userStore';
import {EVENT_TYPE} from "../domain";

const GEOLOCATION_TIMEOUT_MS = 10000;

/**
 * Get the current browser geolocation.
 * Returns { latitude, longitude } or throws an error if unavailable/denied.
 */
const getCurrentGeolocation = () => {
    return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
            reject(new Error("Geolocation is not supported by this browser."));
            return;
        }
        navigator.geolocation.getCurrentPosition(
            (position) => {
                resolve({
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                });
            },
            (error) => {
                switch (error.code) {
                    case error.PERMISSION_DENIED:
                        reject(new Error("Location permission denied. Please enable location access in your browser settings to clock in/out."));
                        break;
                    case error.POSITION_UNAVAILABLE:
                        reject(new Error("Location information is unavailable. Please try again."));
                        break;
                    case error.TIMEOUT:
                        reject(new Error("Location request timed out. Please try again."));
                        break;
                    default:
                        reject(new Error("An unknown error occurred while getting your location."));
                }
            },
            { enableHighAccuracy: true, timeout: GEOLOCATION_TIMEOUT_MS, maximumAge: 60000 }
        );
    });
};

export const fetchUserEventList = async (user_id, callback, callerHint) => {
    try {
        const { currentUser, isUserAuthenticated } = useUserStore.getState();
        if(!isUserAuthenticated){
            loggedAlert("fetchUserEventList: You're faster than the internet! The system hasn't processed your login yet. Please try that again in a few seconds.", currentUser?.user_id)
            return
        }

        const token = await getFirebaseAuthToken();

        const response = await axios.get(`${BACKEND_URL}/timesheet`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            params: { user_id: user_id, limit: 60 },
            timeout: TIMESHEET_POST_TIMEOUT
        });

        callback(user_id, (prev) => response.data); //do nothing with the previous data, just use the response data
    } catch (error) {
        console.error(JSON.stringify(error.toJSON?.() || error));
        const errorMessage = error?.response?.data?.details || error?.message || "Unknown error occurred";
        loggedAlert(`An error occurred fetching user timesheet events for user_id ${user_id}: ${errorMessage}, callerHint: ${callerHint}`, 153, 1, false);
    }
};


export const deleteEventById = async (eventId, userDeletingEvent) => {
    try {
        const { currentUser, isUserAuthenticated } = useUserStore.getState();
        if(!isUserAuthenticated){
            loggedAlert("deleteEventById: You're faster than the internet! The system hasn't processed your login yet. Please try that again in a few seconds.", currentUser?.user_id)
            return
        }

        const token = await getFirebaseAuthToken();

        const response = await axios.delete(
            `${BACKEND_URL}/timesheet/${eventId}?user_id=${userDeletingEvent}`,
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                timeout: TIMESHEET_POST_TIMEOUT,
            }
        );

        return response.data;
    } catch (error) {
        console.error("Delete error:", JSON.stringify(error.toJSON?.() || error));
        const errorMessage = error?.response?.data?.details || error?.message || "Unknown error occurred";
        throw new Error(`Failed to delete event ${eventId}: ${errorMessage}`);
    }
};

export const handleDelete = async (event, idOfUserDeletingTheEvent, setDeletingId, onSuccessCallback) => {
    const { currentUser, isUserAuthenticated } = useUserStore.getState();

    if(!isUserAuthenticated){
        loggedAlert("handleDelete: You're faster than the internet! The system hasn't processed your login yet. Please try that again in a few seconds.", currentUser?.user_id)
        return
    }

    const confirmed = window.confirm("Are you sure you want to delete this event?");
    if (!confirmed) return;

    setDeletingId(event.id);

    try {
        await deleteEventById(event.id, idOfUserDeletingTheEvent);
        onSuccessCallback();
    } catch (error) {
        console.error("Error deleting event:", error);
        loggedAlert("Failed to delete event. Error: " + error.message);
    } finally {
        setDeletingId(null);
    }
};

const _alertThrottle = {};
const ALERT_THROTTLE_MS = 10_000; // max one event per 10s per unique message

export const loggedAlert = async (message, user_id=153, location_id=1, showAlert = true, sendEvent = true) => {
    try {
        if(location_id === null){
            location_id = 1
        }

        if(user_id === null){
            user_id = 153
        }

        console.log(message)

        if(sendEvent){
            const now = Date.now();
            const lastSent = _alertThrottle[message] || 0;
            if (now - lastSent < ALERT_THROTTLE_MS) {
                console.warn(`loggedAlert throttled (duplicate within ${ALERT_THROTTLE_MS}ms): ${message}`);
            } else {
                _alertThrottle[message] = now;
                addEventWithDefaults(
                    () => {},
                    user_id,
                    user_id,
                    "note",
                    location_id,
                    true,
                    Date.now(),
                    Date.now(),
                    message,
                    true
                )
            }
        }

     }catch (error) {
        console.error("Error logging alert:", error);
    } finally {
        if(showAlert){
            alert(message)
        }
    }
}

export const addEventWithDefaults= async (callback, user_id, created_by, event_type, location_id, needs_review=false, event_timestamp=Date.now(), insert_timestamp=Date.now(),  notes="", originatesFromLoggedAlert=false) => {
    const { currentUser, isUserAuthenticated } = useUserStore.getState();

    if(event_type !== EVENT_TYPE.NOTE && !isUserAuthenticated){
        loggedAlert("addEventWithDefaults: type: "+event_type+". You're faster than the internet! The system hasn't processed your login yet. Please try that again in a few seconds.", currentUser?.user_id)
        return
    }

    if (event_type === EVENT_TYPE.NOTE && notes.length === 0) {
        loggedAlert("You can't enter an empty note!");
        return false;
    }

    const event = {
        user_id: user_id,
        event_type: event_type,
        event_timestamp: event_timestamp,
        insert_timestamp: insert_timestamp,
        notes: notes,
        created_by: created_by,
        location_id: location_id,
        needs_review: needs_review,
    }

    return addEvent(event, callback, originatesFromLoggedAlert);
}

export const addEvent = async (event, callback, originatesFromLoggedAlert=false) => {
    const { currentUser, isUserAuthenticated } = useUserStore.getState();

    if(event.event_type !== EVENT_TYPE.NOTE && !isUserAuthenticated){
        if(!originatesFromLoggedAlert){ //don't want a loop of errors caused by an error happening
            loggedAlert("addEvent: You're faster than the internet! The system hasn't processed your login yet. Please try that again in a few seconds.", currentUser?.user_id)
        }

        return false;
    }

    if (event.event_type === EVENT_TYPE.NOTE && event.notes.length === 0) {
        if(!originatesFromLoggedAlert) { //don't want a loop of errors caused by an error happening
            loggedAlert("You can't enter an empty note!");
        }
        return false;
    }

    const payload = {
        user_id: parseInt(event.user_id),
        event_type: event.event_type,
        event_timestamp: event.event_timestamp,
        insert_timestamp: Date.now(),
        notes: event.notes,
        created_by: event.created_by,
        location_id: event.location_id,
        needs_review: event.needs_review,
        value: event.value,
        is_forgotten_event: !!event.is_forgotten_event
    };

    // Capture geolocation for clock in/out (non-forgotten, self-service only)
    const isClockEvent = event.event_type === EVENT_TYPE.CLOCK_IN || event.event_type === EVENT_TYPE.CLOCK_OUT;
    const isSelfService = !event.created_by || parseInt(event.created_by) === parseInt(event.user_id);
    if (isClockEvent && isSelfService && !event.is_forgotten_event) {
        try {
            const geo = await getCurrentGeolocation();
            payload.latitude = geo.latitude;
            payload.longitude = geo.longitude;
        } catch (geoError) {
            // Geolocation unavailable — proceed without it
            console.warn("Geolocation unavailable, submitting event without coordinates:", geoError.message);
        }
    }

    try {
        const token = await getFirebaseAuthToken();

        const response = await axios.post(`${BACKEND_URL}/timesheet`, payload, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            timeout: TIMESHEET_POST_TIMEOUT
        });

        if (response.status === 200) {
            const newEvent = response.data;
            callback(newEvent);
            return true;
        } else {
            if(!originatesFromLoggedAlert) { //don't want a loop of errors caused by an error happening
                loggedAlert(`Error: ${response.data?.error}`);
            }
            return false;
        }
    } catch (error) {
        let errorMessage = "";
        let showErrorToUser = true
        if(error.response){
            // Geofence violations: show clean message directly without payload dump
            if (error.response.status === 403 && error.response?.data?.error && error.response.data.error.includes("on-site")) {
                alert(error.response.data.error);
                return false;
            }

            errorMessage = `AddEvent Server error: ${error.response?.data?.error}, Payload: ${JSON.stringify(payload)}`

            if(error.response?.data?.error && error.response.data.error.includes("must be sequenced after")){
                originatesFromLoggedAlert = true //skip logging the alert if it's due to clocking out before in
            }
        } else {
            //Seems that in all cases so far this is a network latency issue of not getting the response back from the backend BUT
            //The request always seems to make it to the backend and end up in the database
            //Because of that, I'm deciding not to show the network errors to the user via an alert, but still recording them
            errorMessage = `AddEvent Network Error:\n${error.name}: ${error.message}\n\n${error.stack}, Payload: ${JSON.stringify(payload)}`
            showErrorToUser = false
        }

        if(!originatesFromLoggedAlert) { //don't want a loop of errors caused by an error happening
            loggedAlert(errorMessage, parseInt(event.user_id), event.location_id, showErrorToUser, true);
        }
        return false;
    }
}

export const fetchSickEvents = async (callback, start_date = null, end_date = null, callerHint = "") => {
    const { currentUser, isUserAuthenticated } = useUserStore.getState();

    try {
        if (!isUserAuthenticated) {
            loggedAlert("fetchSickEvents: You're faster than the internet! The system hasn't processed your login yet. Please try that again in a few seconds.", currentUser?.user_id);
            return;
        }

        const token = await getFirebaseAuthToken();

        const payload = {};
        if (start_date) payload.start_date = start_date;
        if (end_date) payload.end_date = end_date;

        const response = await axios.post(`${BACKEND_URL}/sick_hours`, payload, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            timeout: TIMESHEET_POST_TIMEOUT
        });

        if (response.status === 200) {
            callback(response.data);
        } else {
            loggedAlert(`Unexpected response fetching sick events: ${response.statusText}`, currentUser?.user_id);
        }

    } catch (error) {
        console.error("fetchSickEvents error:", JSON.stringify(error.toJSON?.() || error));
        const errorMessage = error?.response?.data?.error || error?.message || "Unknown error";
        loggedAlert(`An error occurred while fetching sick events: ${errorMessage}, callerHint: ${callerHint}`, currentUser?.user_id);
    }
};

export const fetchSickHoursRange = async (startDate, endDate) => {
    const token = await getFirebaseAuthToken();
    const payload = {};
    if (startDate) payload.start_date = startDate;
    if (endDate) payload.end_date = endDate;

    const response = await axios.post(`${BACKEND_URL}/sick_hours`, payload, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
        timeout: TIMESHEET_POST_TIMEOUT
    });

    return response.data;
};

export const fetchDailySummary = async (date, userId) => {
    const token = await getFirebaseAuthToken();
    const params = new URLSearchParams();
    if (date) params.append('date', date);
    if (userId) params.append('user_id', userId);

    const response = await axios.get(
        `${BACKEND_URL}/daily-summary?${params.toString()}`,
        {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 60000,
        }
    );
    return response.data;
};

export const fetchUserPermissions = async (userId) => {
    const token = await getFirebaseAuthToken();
    const params = new URLSearchParams();
    params.append('user_id', userId);

    const response = await axios.get(
        `${BACKEND_URL}/permissions?${params.toString()}`,
        {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 10000,
        }
    );
    return response.data;
};

export const fetchDailySummaryRange = async (startDate, endDate, userId) => {
    const token = await getFirebaseAuthToken();
    const params = new URLSearchParams();
    params.append('start_date', startDate);
    params.append('end_date', endDate);
    if (userId) params.append('user_id', userId);

    const response = await axios.get(
        `${BACKEND_URL}/daily-summary-range?${params.toString()}`,
        {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 90000,
        }
    );
    return response.data;
};
